/* eslint no-continue: 0 */
import { format, subMonths } from 'date-fns';

import * as constants from './constants';


const SEARCH_DATE_FORMAT = 'yyyy-MM-dd';
const MAX_ASSETS_MONTHS_OFFSET = 1;


/**
 * Timerange based on assets availability time
 *
 * @return {object{
 *   startDate{date} - first day when assets were created
 *   endDate{date} - last day when assets of assets (should be 'now')
 * }
 */
export const getAssetsTimeOffset = (endDate = new Date()) => {
  const startDate = subMonths(endDate, MAX_ASSETS_MONTHS_OFFSET);
  return { startDate, endDate };
};


/**
 * Append Date filter if doesn't exist
 * @param {string} - query value
 *
 * @return {string} - enchanced query value
 */
export const parsePhrase = (payload) => {
  let finalQuery = payload;
  if (!payload.includes('date:')) {
    const { startDate, endDate } = getAssetsTimeOffset();
    const from = format(startDate, SEARCH_DATE_FORMAT);
    const to = format(endDate, SEARCH_DATE_FORMAT);
    finalQuery += ` AND date: [${from} TO ${to}]`;
  }
  return finalQuery;
};


/**
 * Create a date range string for date filter
 * @param {[]string} - start and end string-dates in YYYY-MM-DD format
 *
 * @return {string} - date range valid for date filter
 */
export const buildDateRange = ([startDate, endDate]) => (
  `[${format(startDate, constants.SEARCH_DATE_FORMAT)} TO ` +
    `${format(endDate, constants.SEARCH_DATE_FORMAT)}]`
);


/**
 * Transform query phrase to valid filter format: [filterName, filterValue]
 * @param {object{
 *   connectorSeparator{string} - last char of the filter - closing bracket
 *   inputSplit{string} - separator between values inside the filter, probably ' ' or '"'
 *   inputKey{string} - filter name
 *   inputValue{string} - filter value
 * }}
 *
 * @return {[]string} - two element array with filter name and value
 */
export const normalizeQueryPhraseToFilters = ({
  connectorSeparator, inputSplit, inputKey, inputValue,
}) => {
  const [val, connector] = inputValue.split(connectorSeparator);
  const inputValues = val.replace('(', '').split(inputSplit).filter((el) => el.trim());
  return [inputKey, `("${inputValues.join('" "')}") ${connector}`];
};


/**
 * Create Date from string
 * @param {string} - string in YYYY-MM-DD format
 *
 * @return {date} - datetime based on input
 */
export const parseDateFromStringWithDashes = (date) => {
  const [year, month, day] = date.split('-');
  return new Date(year, month - 1, day);
};


/**
 * Changes datetime from local to UTC tz
 * @param {date} - date in local tz
 *
 * @return {date} - date in UTC tz
 */
const localDateToUTC = (date) => (
  new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
);


/**
 * Changes date filter to UTC tz
 * @param {string} - value of query input
 *
 * @return {string} - value of query input with UTC tz in date filter
 */
const queryDateRegex = /date\s*:\s*\[\s*([\d-]+)\s+to\s+([\d-]+)\s*\]/i;
export const serializeQueryToUTC = (query) => {
  const datePart = query && query.match(queryDateRegex);
  if (datePart && datePart.length >= 3) {
    const start = parseDateFromStringWithDashes(datePart[1]);
    const end = parseDateFromStringWithDashes(datePart[2]);
    end.setHours(23, 59, 59, 999);
    return query.replace(
      queryDateRegex,
      `date: ${buildDateRange([start, end].map(localDateToUTC))}`,
    );
  }

  return query;
};

export const remapFieldNames = (query) => (
  query.replace(/teams:/g, 'team_id:')
);

export const serializeQuery = (query) => (
  remapFieldNames(serializeQueryToUTC(query))
);


/**
 * Parses value from query input to filters format
 * @param {string} - value of query input
 *
 * @return {array[]} - array with filters
 *
 * @example
 * for param payload = 'text: foo AND name: bar'
 * should return [['text', ['foo']], ['name', ['bar']]]
 */
export const normalizeQueryInputValue = (payload) => {
  if (!payload) {
    return [];
  }

  return payload
    .split(/\sAND|OR\s/gi)
    .map((el) => el.trim().split(/:(.+)?/g))
    .map(([key, val]) => {
      // this statement should be truthy for both: (" and "
      if (val.includes('"')) {
        return [key, val.split('" "')];
      }
      if (val.includes('(')) {
        return [key, val.split(' ')];
      }
      if (key === constants.DATE_FILTER) {
        return [key, val.replace(/[[\]]/g, '').split(' TO ')];
      }
      return [key, val];
    })
    .map(([key, val]) => {
      const filteredVal = Array.isArray(val) ?
        val
          .map((value) => value.trim().replace(/[(")]/g, ''))
          .filter((value) => value) :
        val.trim();
      return [key.toLowerCase(), filteredVal];
    });
};


/**
 * Filters out values from filters to contain only filters with values inside selectedItems key
 * @param {object{}} - filters object
 *
 * @return {array[]} - array with filters with selected Items
 *
 * @example
 * for param payload = {
 *  foo: { selectedItems: ['item'] },
 *  bar: { selectedItems: [], items: ['a', 'b'] },
 * };
 * should return [['foo', ['item']]]
 */
export const getFiltersWithSelectedItems = (payload) => (
  Object.entries(payload).reduce((acc, [filterName, value]) => {
    if (value.selectedItems.length) {
      return [...acc, [filterName, value.selectedItems]];
    }
    return acc;
  }, [])
);


/**
 * Merges value of selected filters with input query string
 * @param {object{
 *   input{string} - value of input query
 *   filters{array[]} - list of filters with selected items
 * }}
 *
 * @return {string} - updated value for query string
 *
 * @example
 * for param payload = {
 *  input: 'owner: norbertsienkiewicz',
 *  filters: [['tag', ['foo123']]],
 * };
 * should return 'owner: norbertsienkiewicz AND tag: ("foo123")'
 */
export const unionQuery = ({ input, filters }) => {
  const inputParts = input
    .split(/(text|name|status|build|owner|platform|browser|tag|date|teams):/gi)
    .filter((el) => el.trim());

  const usedFilterNamesInInput = [];
  const existingFilters = [];

  /*
    We only want to consume from the input values from allowed phrases but not filters.
    Filters should contain newest changes applied by updateViaQueryInput method, so we can
    easily attach them, without any merging logic.
  */
  for (let i = 0; i < inputParts.length; i += 2) {
    const inputKey = inputParts[i].toLowerCase();
    const inputValue = inputParts[i + 1] || '';
    if (!constants.ALLOWED_PHRASES_BUT_NOT_FILTERS.includes(inputKey)) {
      continue;
    }

    usedFilterNamesInInput.push(inputKey);

    if (inputKey === constants.DATE_FILTER) {
      return [inputKey, inputValue];
    }

    if (inputValue.includes('")')) {
      const filterElement = normalizeQueryPhraseToFilters({
        connectorSeparator: '")', inputSplit: '"', inputKey, inputValue,
      });
      existingFilters.push(filterElement);
      continue;
    }

    if (inputValue.includes('(')) {
      const filterElement = normalizeQueryPhraseToFilters({
        connectorSeparator: ')', inputSplit: ' ', inputKey, inputValue,
      });
      existingFilters.push(filterElement);
      continue;
    }

    let trimmedValue = inputValue.trim();
    let connector = '';

    // remove connector from value to compare with values in filters
    const hasANDConnector = /\s+AND\s*$/gi.test(trimmedValue);
    const hasORConnector = /\s+OR\s*$/gi.test(trimmedValue);

    if (hasANDConnector) {
      trimmedValue = inputValue.replace(/\s+AND/gi, '').trim();
      connector = ' AND ';
    } else if (hasORConnector) {
      trimmedValue = inputValue.replace(/\s+OR/gi, '').trim();
      connector = ' OR ';
    }

    existingFilters.push([inputKey, `${trimmedValue}${connector}`]);
  }

  // attach keys from filters which doesnt exist in inputquery
  const additionalFilters = filters
    // eslint-disable-next-line no-unused-vars
    .filter(([filterName, values]) => !usedFilterNamesInInput.includes(filterName))
    .map(([filterName, values]) => {
      if (filterName === constants.DATE_FILTER) {
        return [filterName, buildDateRange(values)];
      }
      return [filterName, `("${values.join('" "')}")`];
    });

  // convert filters to string
  const result = [
    existingFilters.map((condition) => condition.join(': ')).join(' '),
  ];

  if (additionalFilters.length) {
    if (usedFilterNamesInInput.length && !/AND\s?$/gi.test(result)) {
      result.push(' AND ');
    }
    result.push(additionalFilters.map((condition) => condition.join(': ')).join(' AND '));
  }

  return result.join('').trim().replace(/\s\s/g, ' ');
};
